import ProductList from "@/components/product/ProductList";
import DownloadApp from "@/components/shared/DownloadApp";
import Loading from "@/components/shared/Loading";
import { ENV } from "@/core/config/env";
import { GET_PRODUCTS } from "@/core/graphql/queries/getProductList";
import { ProductItem } from "@/core/models/products/productList";
import { SearchProductRequest } from "@/core/models/products/productRequest";
import { initializeApollo } from "@/lib/apolloClient";
import { getString } from "@/lib/stringUtils";
import { trackViewItemList } from "@/lib/gtm";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";

interface ProductPageProps {
    products: ProductItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

export default function ProductPage({ products, pagination }: ProductPageProps) {
    const router = useRouter();
    const { query, isReady } = router;

    useEffect(() => {
        if (products?.length) {
            trackViewItemList(products, 'product_list', 'Car Parts & Spares');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isReady) return <Loading />; // prevent undefined query on first render

    const searchParams = {
        category: getString(query.category),
        scope: getString(query.scope),
        saleType: getString(query.saleType),
        text: getString(query.text),
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const scopeTitles: Record<string, string> = {
        featured: 'Featured Car Parts & Spares | Top-Rated Auto Parts South Africa | CarSparesEzyFind',
        special: 'Special Deals on Car Parts & Spares | Discounted Auto Parts South Africa | CarSparesEzyFind',
        normal: 'All Car Parts & Spares | Buy Auto Parts Online South Africa | CarSparesEzyFind',
    };
    const scopeDescriptions: Record<string, string> = {
        featured: 'Shop featured car parts and spares handpicked for quality and value. Browse top-rated OEM and aftermarket auto parts from trusted suppliers across South Africa. Buy, bid, or hire automotive products on CarSparesEzyFind.',
        special: 'Browse exclusive special deals and discounted car parts across South Africa. Save on quality OEM and aftermarket spares — engine parts, brake systems, suspension, tyres, batteries and more. Limited-time offers on CarSparesEzyFind.',
        normal: 'Browse all car parts and spares available online in South Africa. Find new and used OEM components, aftermarket parts, engine parts, brake systems, suspension, batteries, tyres and more from verified automotive suppliers.',
    };
    const scopeKeywords: Record<string, string> = {
        featured: 'featured car parts South Africa, top rated auto parts, best car spares, quality OEM parts, premium aftermarket car parts, CarSparesEzyFind featured',
        special: 'car parts deals South Africa, discounted auto parts, cheap car spares, special offers car parts, sale auto parts, clearance car spares',
        normal: 'buy car parts online South Africa, car spares online, auto parts, OEM parts, aftermarket parts, engine parts, brake parts, suspension parts',
    };

    const scope = searchParams.scope ?? '';
    const title = scopeTitles[scope] ?? 'Car Parts & Spares | Buy, Bid & Hire Auto Parts South Africa | CarSparesEzyFind';
    const description = scopeDescriptions[scope] ?? 'Purchase, bid, and hire car parts and auto products from verified suppliers across South Africa. Find new and used OEM components, aftermarket accessories, and automotive services on CarSparesEzyFind.';
    const keywords = scopeKeywords[scope] ?? 'buy car parts online, car spares South Africa, auto parts, OEM parts, aftermarket parts, bid car parts, hire automotive services';

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="title" content={title} />
                <meta name='description' content={description} />
                <meta name='keywords' content={keywords} />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="CarSparesEzyFind" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="geo.region" content="ZA" />
                <meta name="geo.placename" content="South Africa" />
            </Head>
            <div className="my-10">
                <div className="container">
                    <ProductList
                        searchParams={searchParams}
                        initialProducts={products}
                        initialPagination={pagination}
                    />
                </div>
            </div>
            <DownloadApp />
        </>
    );
}


export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const searchProductRequest: SearchProductRequest = {
        domainCategoryIds: String(ENV.CATEGORY_ID),
        page: 1,
        size: 12,
        salesTypeId: null,
        scopeId: null
    };

    const { data: productData } = await apolloClient.query({
        query: GET_PRODUCTS,
        variables: searchProductRequest,
    });

    return {
        props: {
            products: productData?.getPrdProductList?.result ?? [],
            pagination: {
                count: productData?.getPrdProductList?.count ?? 0,
                currentPage: productData?.getPrdProductList?.currentPage ?? 1,
                totalPages: productData?.getPrdProductList?.totalPages ?? 0,
            },
        }
    };
};